/**
 * Troca o bundle velho pelo novo depois de um deploy, sem esperar o usuario dar F5.
 *
 * O PWA usa `registerType: 'autoUpdate'` (skipWaiting + clientsClaim): o sw.js novo assume
 * a pagina sozinho, mas o JS que ja esta rodando continua sendo o da versao anterior. Sem
 * isso, guardas novas (ex.: o bloqueio do contrato) so valem a partir do proximo carregamento.
 *
 * Recarregar na hora apagaria o que o usuario estiver digitando (OTP do login, formularios).
 * Por isso a troca fica para um momento em que nao se perde nada:
 * - na proxima navegacao do SPA (a tela atual ja esta sendo deixada);
 * - ou quando a aba fica oculta.
 *
 * A primeira instalacao do SW tambem dispara `controllerchange` (clientsClaim), mas ai a
 * pagina veio da rede e ja e a versao atual — sem controller no inicio, nada a fazer.
 */

export interface SwUpdateEnv {
  serviceWorker: Pick<ServiceWorkerContainer, 'controller' | 'addEventListener'> | undefined
  document: Pick<Document, 'visibilityState' | 'addEventListener'>
  reload: () => void
}

export interface SwUpdateReload {
  /** Chamar a cada mudanca de rota; recarrega se uma versao nova ja assumiu. */
  onNavigation: () => void
}

export function installSwUpdateReload(env: SwUpdateEnv): SwUpdateReload {
  let pending = false
  let reloaded = false

  const reloadOnce = () => {
    if (reloaded) return
    reloaded = true
    env.reload()
  }

  const container = env.serviceWorker
  if (container?.controller) {
    container.addEventListener('controllerchange', () => {
      pending = true
      if (env.document.visibilityState === 'hidden') reloadOnce()
    })
    env.document.addEventListener('visibilitychange', () => {
      if (pending && env.document.visibilityState === 'hidden') reloadOnce()
    })
  }

  return {
    onNavigation: () => {
      if (pending) reloadOnce()
    },
  }
}

export function installBrowserSwUpdateReload(): SwUpdateReload {
  return installSwUpdateReload({
    serviceWorker: typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined,
    document,
    reload: () => window.location.reload(),
  })
}
